/**
 * CD ladder builder — Phase 8 Session 3.
 *
 * Pure-math helper. Generates an N-rung CD ladder with equal capital
 * per rung, terms stepping `term_step_months` apart starting from
 * `base_term_months`, and per-rung APY interpolated from a yield-curve
 * function (caller supplies the function — defaults to a flat curve
 * if missing).
 *
 * Common configurations:
 *   - 1-yr ladder, 4 rungs: terms 3/6/9/12 months
 *   - 5-yr ladder, 5 rungs: terms 12/24/36/48/60 months
 *   - 10-yr ladder, 10 rungs: terms 12, 24, ..., 120 months
 */

export interface CdLadderRung {
  readonly index: number;
  readonly term_months: number;
  readonly principal_cents: number;
  readonly apy_bp: number;
  readonly maturity_date: string; // ISO yyyy-mm-dd
  readonly projected_interest_cents: number;
  readonly projected_value_cents: number;
}

export interface CdLadderResult {
  readonly rungs: readonly CdLadderRung[];
  readonly total_principal_cents: number;
  readonly total_projected_interest_cents: number;
  readonly total_projected_value_cents: number;
  readonly weighted_avg_apy_bp: number;
}

/**
 * Default flat-curve rate function — returns the same rate regardless
 * of term. Caller can pass a real curve (e.g., from Treasury par-yield
 * snapshots) for production-grade laddering.
 */
export function flatRateCurve(apy_bp: number): (term_months: number) => number {
  return () => apy_bp;
}

export function buildCdLadder(args: {
  readonly total_cents: number;
  readonly rungs: number;
  readonly base_term_months: number;
  readonly term_step_months: number;
  readonly rate_curve: (term_months: number) => number;
  readonly start_date?: string;
}): CdLadderResult {
  if (args.rungs <= 0) {
    throw new Error("buildCdLadder: rungs must be > 0");
  }
  if (args.total_cents < args.rungs) {
    throw new Error("buildCdLadder: total_cents must be ≥ rungs");
  }
  const start = args.start_date
    ? new Date(args.start_date)
    : new Date(new Date().toISOString().slice(0, 10));
  const baseRung = Math.floor(args.total_cents / args.rungs);
  const remainder = args.total_cents - baseRung * args.rungs;

  const rungs: CdLadderRung[] = [];
  for (let i = 0; i < args.rungs; i += 1) {
    const term = args.base_term_months + i * args.term_step_months;
    const apy = args.rate_curve(term);
    // Last rung absorbs the cent-level rounding remainder so the sum
    // matches `total_cents` exactly.
    const principal = i === args.rungs - 1 ? baseRung + remainder : baseRung;
    const yearsFraction = term / 12;
    const interest = Math.round((principal * apy * yearsFraction) / 10000);
    const maturity = addMonths(start, term);
    rungs.push({
      index: i,
      term_months: term,
      principal_cents: principal,
      apy_bp: apy,
      maturity_date: maturity.toISOString().slice(0, 10),
      projected_interest_cents: interest,
      projected_value_cents: principal + interest,
    });
  }

  const totalPrincipal = rungs.reduce(
    (acc, r) => acc + r.principal_cents,
    0,
  );
  const totalInterest = rungs.reduce(
    (acc, r) => acc + r.projected_interest_cents,
    0,
  );
  // Weighted by principal × term-years.
  let weightedNum = 0;
  let weightedDen = 0;
  for (const r of rungs) {
    const w = r.principal_cents * (r.term_months / 12);
    weightedNum += w * r.apy_bp;
    weightedDen += w;
  }
  const weightedAvg = weightedDen === 0 ? 0 : Math.round(weightedNum / weightedDen);

  return {
    rungs,
    total_principal_cents: totalPrincipal,
    total_projected_interest_cents: totalInterest,
    total_projected_value_cents: totalPrincipal + totalInterest,
    weighted_avg_apy_bp: weightedAvg,
  };
}

function addMonths(d: Date, months: number): Date {
  const out = new Date(d);
  out.setUTCMonth(out.getUTCMonth() + months);
  return out;
}
